import { Check, Loader2, X } from "lucide-react";
import { Sheet, SheetDescription, SheetHeader, SheetTitle, cn } from "@eduviaos/ui-kit";
import { ResponsiveSheetContent } from "../ResponsiveSheetContent";
import { useTrackRecordDetail } from "../../lib/learning/hooks";
import type { GradedQuestion, TrackRecordEntry } from "../../lib/learning/types";
import { usePortalLocale } from "../../lib/i18n/usePortalLocale";

export function TrackRecordDetailSheet({
  entry,
  open,
  onOpenChange,
}: {
  entry: TrackRecordEntry | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { strings: s } = usePortalLocale();
  const detailQuery = useTrackRecordDetail(entry?.id ?? null, open && Boolean(entry));

  const detail = detailQuery.data?.detail;
  const questions = (detail?.questions ?? []) as GradedQuestion[];

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <ResponsiveSheetContent className="max-h-[90vh] overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{entry?.title ?? s.trackRecordActivityDetail}</SheetTitle>
          <SheetDescription>{entry?.summary ?? s.trackRecordSessionBreakdown}</SheetDescription>
        </SheetHeader>

        <div className="mt-4 space-y-4">
          {detailQuery.isLoading ? (
            <div className="flex justify-center py-10">
              <Loader2
                className="text-muted-foreground size-5 animate-spin"
                aria-label={s.loadingAria}
              />
            </div>
          ) : detailQuery.isError ? (
            <p className="text-destructive text-sm">{s.trackRecordLoadFailed}</p>
          ) : !detail ? (
            <p className="text-muted-foreground text-sm">{s.trackRecordNoDetail}</p>
          ) : (
            <>
              {typeof detail.score === "number" && (
                <p className="text-sm font-semibold tabular-nums">
                  {s.trackRecordScoreLabel(Math.round(detail.score))}
                </p>
              )}
              {detail.grade_reconciled && detail.effective_quality != null && (
                <p className="bg-joy-amber/10 text-joy-amber rounded-lg px-3 py-2 text-xs">
                  {s.trackRecordEffectiveRecall(
                    detail.effective_quality,
                    detail.self_reported_quality,
                  )}
                </p>
              )}
              {questions.length > 0 && (
                <ul className="divide-border/60 space-y-3 divide-y">
                  {questions.map((q) => (
                    <li key={q.id} className="space-y-2 pt-3 first:pt-0">
                      <div className="flex items-start gap-2">
                        {q.is_correct ? (
                          <Check className="text-joy-green mt-0.5 size-4 shrink-0" />
                        ) : (
                          <X className="text-destructive mt-0.5 size-4 shrink-0" />
                        )}
                        <p className="text-sm leading-relaxed">{q.prompt}</p>
                      </div>
                      <ul className="ml-6 space-y-1">
                        {q.options.map((opt) => {
                          const isCorrect = opt.id === q.correct_option_id;
                          const isSelected = opt.id === q.selected_option_id;
                          return (
                            <li
                              key={opt.id}
                              className={cn(
                                "text-xs",
                                isCorrect && "text-joy-green font-medium",
                                isSelected && !isCorrect && "text-destructive",
                                !isSelected && !isCorrect && "text-muted-foreground",
                              )}
                            >
                              {opt.id}. {opt.label}
                              {isSelected ? s.trackRecordYourPick : ""}
                              {isCorrect ? s.trackRecordCorrect : ""}
                            </li>
                          );
                        })}
                      </ul>
                      {q.explanation && (
                        <p className="text-muted-foreground ml-6 text-xs leading-relaxed">
                          {q.explanation}
                        </p>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </div>
      </ResponsiveSheetContent>
    </Sheet>
  );
}
